/* eslint-disable react/prop-types */
import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Wand2, Undo2 } from "lucide-react";
import { toast } from "sonner";

export default function AIAutoFitDialog({ open, onOpenChange, resumeId, currentData, onApply }) {
  const [direction, setDirection] = useState("auto");
  const [tone, setTone] = useState("professional");
  const [aggression, setAggression] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const run = async (overflowSignal) => {
    setBusy(true); setPreview(null);
    try {
      const r = await api.post(`/resumes/${resumeId}/ai-autofit`, { direction, tone, aggression, overflow_signal: overflowSignal || null });
      setPreview(r.data.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-fit failed"); } finally { setBusy(false); }
  };

  const apply = () => {
    if (preview) onApply(preview);
    onOpenChange(false);
    toast.success("AI Auto-Fit applied");
    setPreview(null);
  };

  const undo = () => { setPreview(null); };

  const diff = preview ? buildDiff(currentData, preview) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="size-4" /> AI Auto-Fit</DialogTitle>
          <DialogDescription>Choose how the AI should rebalance your resume content. We'll show a preview before applying.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Direction">
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger data-testid="autofit-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-balance</SelectItem>
                <SelectItem value="trim">Trim (too long)</SelectItem>
                <SelectItem value="expand">Expand (too short)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tone">
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="autofit-tone"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="strong">Strong</SelectItem>
                <SelectItem value="concise">Concise</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Aggression">
            <Select value={aggression} onValueChange={setAggression}>
              <SelectTrigger data-testid="autofit-aggression"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        {!preview && (
          <div className="rounded-md border border-dashed border-border p-5 text-center">
            <div className="text-sm text-muted-foreground mb-3">AI will rewrite content to match the page — click below to generate a preview.</div>
            <Button onClick={() => run()} disabled={busy} className="gap-2 lift" data-testid="autofit-run">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Wand2 className="size-4" />} Generate Auto-Fit preview
            </Button>
          </div>
        )}

        {preview && diff && (
          <div className="border border-border rounded-md p-4 max-h-[40vh] overflow-y-auto" data-testid="autofit-diff">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">Preview of changes</div>
            <div className="space-y-2 text-sm">
              {diff.summaryChanged && (
                <DiffRow label="Summary" before={currentData.summary} after={preview.summary} />
              )}
              {diff.expChanged && (
                <DiffRow label="Experience bullets" before={`${diff.beforeExpCount} bullets`} after={`${diff.afterExpCount} bullets`} />
              )}
              {diff.compsChanged && (
                <DiffRow label="Competencies" before={(currentData.competencies || []).length + " tags"} after={(preview.competencies || []).length + " tags"} />
              )}
              {diff.highChanged && (
                <DiffRow label="Highlights" before={(currentData.highlights || []).length + " items"} after={(preview.highlights || []).length + " items"} />
              )}
              {!diff.anyChange && <div className="text-muted-foreground">No structural changes detected. Content was lightly polished.</div>}
            </div>
          </div>
        )}

        <DialogFooter>
          {preview && <Button variant="outline" onClick={undo} className="gap-2" data-testid="autofit-undo"><Undo2 className="size-4" /> Discard</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="autofit-cancel">Cancel</Button>
          {preview && <Button onClick={apply} className="lift gap-2" data-testid="autofit-apply"><Wand2 className="size-4" /> Apply changes</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs font-semibold">{label}</Label><div className="mt-1.5">{children}</div></div>;
}

function DiffRow({ label, before, after }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-start">
      <div className="col-span-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className="col-span-5 bg-destructive/10 border border-destructive/20 rounded-sm p-2 text-xs"><span className="font-bold mr-1">Before</span>{String(before).slice(0, 200)}</div>
      <div className="col-span-5 bg-primary/10 border border-primary/20 rounded-sm p-2 text-xs"><span className="font-bold mr-1">After</span>{String(after).slice(0, 200)}</div>
    </div>
  );
}

function buildDiff(before, after) {
  const beforeExp = (before.experiences || []).reduce((n, e) => n + (e.bullets || []).length, 0);
  const afterExp = (after.experiences || []).reduce((n, e) => n + (e.bullets || []).length, 0);
  const summaryChanged = (before.summary || "") !== (after.summary || "");
  const expChanged = beforeExp !== afterExp;
  const compsChanged = JSON.stringify(before.competencies || []) !== JSON.stringify(after.competencies || []);
  const highChanged = JSON.stringify(before.highlights || []) !== JSON.stringify(after.highlights || []);
  return { summaryChanged, expChanged, beforeExpCount: beforeExp, afterExpCount: afterExp, compsChanged, highChanged, anyChange: summaryChanged || expChanged || compsChanged || highChanged };
}
